/**
 * User Settings API
 *
 * Frontend API for persisting user preferences to PostgreSQL backend.
 * Replaces localStorage for cross-device/session persistence.
 */

import { apiClient } from './apiClient';
import { logger } from '@shared/utils/logger';

/**
 * Get a user setting by key
 *
 * @param key - Setting key (e.g., 'last_selected_campaign')
 * @returns Setting value or null if not found
 */
export const getUserSetting = async <T = unknown>(key: string): Promise<T | null> => {
  try {
    const response = await apiClient.get<{ key: string; value: T | null }>(`/user-settings/${key}`);
    return response.data.value;
  } catch (error) {
    logger.error(`[UserSettingsAPI] Failed to get setting "${key}":`, error);
    return null;
  }
};

/**
 * Set a user setting
 *
 * @param key - Setting key
 * @param value - Value to store
 * @returns Success status
 */
export const setUserSetting = async <T = unknown>(key: string, value: T): Promise<boolean> => {
  try {
    await apiClient.put(`/user-settings/${key}`, { value });
    logger.log(`[UserSettingsAPI] Set ${key} = ${JSON.stringify(value)}`);
    return true;
  } catch (error) {
    logger.error(`[UserSettingsAPI] Failed to set setting "${key}":`, error);
    return false;
  }
};

/**
 * Delete a user setting
 *
 * @param key - Setting key to delete
 * @returns Success status
 */
export const deleteUserSetting = async (key: string): Promise<boolean> => {
  try {
    await apiClient.delete(`/user-settings/${key}`);
    logger.log(`[UserSettingsAPI] Deleted setting "${key}"`);
    return true;
  } catch (error) {
    logger.error(`[UserSettingsAPI] Failed to delete setting "${key}":`, error);
    return false;
  }
};

// Convenience functions for common settings

/**
 * Get the last selected campaign ID from PostgreSQL
 *
 * @returns Campaign ID or null if not saved
 */
export const getLastSelectedCampaign = async (): Promise<string | null> => {
  try {
    const response = await apiClient.get<{ campaign_id: string | null }>('/user-settings/campaign/last-selected');
    return response.data.campaign_id;
  } catch (error) {
    logger.error('[UserSettingsAPI] Failed to get last selected campaign:', error);
    return null;
  }
};

/**
 * Save the last selected campaign ID to PostgreSQL
 *
 * @param campaignId - Campaign ID to save
 * @returns Success status
 */
export const setLastSelectedCampaign = async (campaignId: string): Promise<boolean> => {
  try {
    await apiClient.put('/user-settings/campaign/last-selected', { campaign_id: campaignId });
    logger.log(`[UserSettingsAPI] Saved last selected campaign: ${campaignId}`);
    return true;
  } catch (error) {
    logger.error('[UserSettingsAPI] Failed to save last selected campaign:', error);
    return false;
  }
};

// ==================== Active Route Per Campaign ====================

/**
 * Get the active route ID for a specific campaign from PostgreSQL
 *
 * @param campaignId - Campaign identifier
 * @returns Route ID or null if not set
 */
export const getActiveRoute = async (campaignId: string): Promise<string | null> => {
  try {
    const response = await apiClient.get<{ campaign_id: string; route_id: string | null }>(
      `/user-settings/campaign/${campaignId}/active-route`
    );
    return response.data.route_id;
  } catch (error) {
    logger.error(`[UserSettingsAPI] Failed to get active route for ${campaignId}:`, error);
    return null;
  }
};

/**
 * Save the active route ID for a specific campaign to PostgreSQL
 *
 * @param campaignId - Campaign identifier
 * @param routeId - Route ID to save
 * @returns Success status
 */
export const setActiveRoute = async (campaignId: string, routeId: string): Promise<boolean> => {
  try {
    await apiClient.put(`/user-settings/campaign/${campaignId}/active-route`, { route_id: routeId });
    logger.log(`[UserSettingsAPI] Saved active route for ${campaignId}: ${routeId}`);
    return true;
  } catch (error) {
    logger.error(`[UserSettingsAPI] Failed to save active route for ${campaignId}:`, error);
    return false;
  }
};

/**
 * Clear the active route for a specific campaign
 *
 * @param campaignId - Campaign identifier
 * @returns Success status
 */
export const clearActiveRoute = async (campaignId: string): Promise<boolean> => {
  try {
    await apiClient.delete(`/user-settings/campaign/${campaignId}/active-route`);
    logger.log(`[UserSettingsAPI] Cleared active route for ${campaignId}`);
    return true;
  } catch (error) {
    logger.error(`[UserSettingsAPI] Failed to clear active route for ${campaignId}:`, error);
    return false;
  }
};

export default {
  getUserSetting,
  setUserSetting,
  deleteUserSetting,
  getLastSelectedCampaign,
  setLastSelectedCampaign,
  getActiveRoute,
  setActiveRoute,
  clearActiveRoute,
};
